
import React, { useState } from 'react';
import { Bell, Filter, Check, Settings, Calendar, DollarSign, Users, Briefcase, Heart, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import NotificationCard from './NotificationCard';
import NotificationSettings from './NotificationSettings';
import { useNotifications, groupNotificationsByDate } from "@/hooks/useNotifications";
import { useMarkAllNotificationsRead } from "@/hooks/useNotificationMutations";

const NotificationsScreen = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const filters = [
    { id: 'all', label: 'All', icon: Bell },
    { id: 'campaigns', label: 'Campaigns', icon: Briefcase },
    { id: 'influencers', label: 'Influencers', icon: Users },
    { id: 'finance', label: 'Finance', icon: DollarSign },
    { id: 'tasks', label: 'Tasks', icon: Check },
    { id: 'relationship', label: 'Relationship', icon: Heart },
    { id: 'system', label: 'System', icon: Zap },
  ];

  const { data: notifications = [], isLoading, error } = useNotifications({
    filterType: activeFilter,
    unreadOnly
  });

  const markAllRead = useMarkAllNotificationsRead();

  const unreadCount = notifications.filter(n => !n.read).length;

  const groupedNotifications = groupNotificationsByDate(notifications);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <div className="flex-1 flex flex-col">
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Bell className="w-6 h-6 text-purple-600" />
              <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
              {unreadCount > 0 && (
                <Badge variant="destructive" className="rounded-full">
                  {unreadCount}
                </Badge>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending || unreadCount === 0}
              >
                <Check className="w-4 h-4 mr-2" />
                {markAllRead.isPending ? "Marking..." : "Mark All Read"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(!showSettings)}
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 overflow-x-auto">
              {filters.map((filter) => {
                const Icon = filter.icon;
                const isActive = activeFilter === filter.id;
                return (
                  <Button
                    key={filter.id}
                    type="button"
                    // variant={activeFilter === filter.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setActiveFilter(filter.id)}
                    // className="whitespace-nowrap"
      //               className={
      //   isActive
      //     ? "whitespace-nowrap bg-purple-600 text-white"
      //     : "whitespace-nowrap bg-gray-100 hover:bg-gray-200"
      // }
      className={
    (isActive
      ? "whitespace-nowrap bg-purple-600 text-white font-medium rounded-lg active:bg-purple-600 focus:bg-purple-600 focus:text-white active:text-white"
      : "whitespace-nowrap bg-gray-100 text-black hover:bg-gray-200 font-medium rounded-lg active:bg-gray-200 focus:bg-gray-100"
    ) + " flex items-center transition-colors"
  }
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {filter.label}
                  </Button>
                );
              })}
            </div>
            <div className="flex items-center space-x-2 ml-4">
              <Switch
                checked={unreadOnly}
                onCheckedChange={setUnreadOnly}
                id="unread-only"
              />
              <label htmlFor="unread-only" className="text-sm font-medium text-gray-700">
                Unread Only
              </label>
            </div>
          </div>
        </div>

        <div className="flex-1 p-6 overflow-y-auto">
          {isLoading && (
            <div className="text-center py-12">
              <span className="text-gray-500">Loading notifications…</span>
            </div>
          )}
          {error && (
            <div className="text-center py-12 text-red-500">
              Error loading notifications.
            </div>
          )}

          {!isLoading && !error && groupedNotifications.today.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Today</h2>
              <div className="space-y-3">
                {groupedNotifications.today.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    notification={notification}
                  />
                ))}
              </div>
            </div>
          )}

          {!isLoading && !error && groupedNotifications.yesterday.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Yesterday</h2>
              <div className="space-y-3">
                {groupedNotifications.yesterday.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    notification={notification}
                  />
                ))}
              </div>
            </div>
          )}

          {!isLoading && !error && groupedNotifications.older.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Earlier</h2>
              <div className="space-y-3">
                {groupedNotifications.older.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    notification={notification}
                  />
                ))}
              </div>
            </div>
          )}

          {!isLoading && !error && notifications.length === 0 && (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No notifications
              </h3>
              <p className="text-gray-600">
                {unreadOnly
                  ? (activeFilter === 'all'
                      ? "No unread notifications at the moment."
                      : `No unread notifications for this category yet.`)
                  : (activeFilter === 'all'
                      ? "You're all caught up!"
                      : `No notifications for this category yet.`)
                }
              </p>
            </div>
          )}
        </div>
      </div>
      {showSettings && (
        <NotificationSettings onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
};

export default NotificationsScreen;
